import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient, type ApDemoMessage, type ApDemoOverview } from '../lib/api-client';
import { ApCasePack } from '../components/ap/ApCasePack';
import { Button } from '../components/ui/Button';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function MailboxPage() {
  const [overview, setOverview] = useState<ApDemoOverview | null>(null);
  const [selected, setSelected] = useState<ApDemoMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('YES. Proof attached. Please continue.');

  const inbox = useMemo(() => (overview?.mailbox ?? []).filter((message) => message.folder === 'inbox'), [overview]);
  const sent = useMemo(() => (overview?.mailbox ?? []).filter((message) => message.folder === 'sent'), [overview]);
  const selectedCase = useMemo(() => {
    const id = selected?.caseId;
    if (!id) return null;
    return overview?.cases.find((row) => row.case_id === id) ?? null;
  }, [overview, selected]);
  const caseAudit = useMemo(() => {
    const id = selected?.caseId;
    if (!id) return [];
    return (overview?.audit ?? []).filter((entry) => entry.case_id === id);
  }, [overview, selected]);

  function reload() {
    void apiClient.getApDemoOverview().then(setOverview).catch(() => setOverview(null));
  }

  useEffect(() => {
    reload();
  }, []);

  async function enablePack() {
    try {
      await apiClient.enableApDemo();
      reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not enable pack'));
    }
  }

  async function drop(scenarioId: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.receiveApInvoice(scenarioId);
      const next = await apiClient.getApDemoOverview();
      setOverview(next);
      const pick = res.hitl ?? res.inbound;
      setSelected(next.mailbox.find((message) => message.id === pick.id) ?? pick);
    } catch (err) {
      setError(errorMessage(err, 'Receive failed'));
    } finally {
      setBusy(false);
    }
  }

  async function reply(decision: 'APPROVE' | 'REJECT') {
    if (!selected) return;
    try {
      await apiClient.replyApMail(selected.id, replyBody, decision);
      const next = await apiClient.getApDemoOverview();
      setOverview(next);
      const fresh = next.mailbox.find((row) => row.id === selected.id);
      if (fresh) setSelected(fresh);
    } catch (err) {
      setError(errorMessage(err, 'Reply failed'));
    }
  }

  async function resetDemo() {
    await apiClient.resetApDemo();
    setSelected(null);
    reload();
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Mailbox</h1>
          <p className="text-sm text-gray-500">Illustrative invoice ingress. This mailbox stands in for SAP, ERP, or email. No live Graph poller.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/payments" data-testid="mailbox-to-payments"><Button size="sm" variant="ghost">Payment orders</Button></Link>
          <Button size="sm" variant="ghost" type="button" onClick={() => void resetDemo()} data-testid="mailbox-reset">Reset demo</Button>
        </div>
      </div>
      {!overview?.packEnabled ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <span>AP Invoice-to-Pay pack is not enabled for this tenant.</span>
          <Button size="sm" type="button" onClick={() => void enablePack()} data-testid="mailbox-enable">Enable illustrative AP pack</Button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-xs text-gray-500">Address: <code>{overview.mailboxAddress}</code></p>
          <section className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
            <h2 className="text-sm font-semibold">Drop invoice PDF</h2>
            <p className="mt-1 text-xs text-gray-500">Each row is a fixture PDF (SC-xx.pdf). Processing uses the deterministic match engine. The agent does not pay.</p>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {overview.scenarios.map((scenario) => (
                <button
                  key={scenario.scenario_id}
                  type="button"
                  disabled={busy}
                  onClick={() => void drop(scenario.scenario_id)}
                  data-testid={`drop-${scenario.scenario_id}`}
                  className="rounded-md border border-gray-300 px-3 py-2 text-left text-sm disabled:opacity-50 dark:border-gray-600"
                >
                  <span className="block font-medium">{scenario.attachment_label} · {scenario.supplier_name}</span>
                  <span className="block text-xs text-gray-500">{scenario.gross_total} {scenario.currency} · expected {scenario.expected_status}</span>
                </button>
              ))}
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </section>
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <section>
              <h2 className="mb-2 text-sm font-semibold">Inbox</h2>
              <ul className="space-y-2">
                {inbox.length === 0 && <li className="text-sm text-gray-400">No inbound invoices yet.</li>}
                {inbox.map((message) => (
                  <li key={message.id}>
                    <button type="button" className={`w-full rounded-lg border bg-white p-3 text-left text-sm dark:bg-gray-900 ${selected?.id === message.id ? 'border-blue-600' : 'border-gray-200 dark:border-gray-700'}`} onClick={() => setSelected(message)} data-testid={`mail-inbox-${message.id}`}>
                      <div className="font-medium">{message.subject}</div>
                      <div className="text-xs text-gray-500">{message.fromAddress} · {message.attachmentLabel}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2 className="mb-2 text-sm font-semibold">Sent (HITL)</h2>
              <ul className="space-y-2">
                {sent.length === 0 && <li className="text-sm text-gray-400">No exception mail yet.</li>}
                {sent.map((message) => (
                  <li key={message.id}>
                    <button type="button" className={`w-full rounded-lg border bg-white p-3 text-left text-sm dark:bg-gray-900 ${selected?.id === message.id ? 'border-blue-600' : 'border-gray-200 dark:border-gray-700'}`} onClick={() => setSelected(message)} data-testid={`mail-sent-${message.id}`}>
                      <div className="font-medium">{message.subject}</div>
                      <div className="text-xs text-gray-500">To {message.toAddress} · {message.status}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          {selected && (
            <article className="mt-5 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900" data-testid="mail-detail">
              <h2 className="text-lg font-semibold">{selected.subject}</h2>
              <p className="text-xs text-gray-500">From {selected.fromAddress} to {selected.toAddress}</p>
              {selected.ccAddress && <p className="text-xs text-gray-500">CC {selected.ccAddress}</p>}
              <pre className="mt-3 whitespace-pre-wrap text-sm">{selected.body}</pre>
              {selectedCase && <div className="mt-4"><ApCasePack caseRow={selectedCase} audit={caseAudit} /></div>}
              {selected.folder === 'sent' && selected.status === 'awaiting_reply' && (
                <div className="mt-4 flex flex-col gap-2">
                  <textarea className="rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-800" rows={3} value={replyBody} onChange={(event) => setReplyBody(event.target.value)} data-testid="mail-reply-body" />
                  <div className="flex gap-2">
                    <Button size="sm" type="button" onClick={() => void reply('APPROVE')} data-testid="mail-reply-yes">Reply YES (resume)</Button>
                    <Button size="sm" variant="outline" type="button" onClick={() => void reply('REJECT')} data-testid="mail-reply-no">Reply NO</Button>
                  </div>
                </div>
              )}
            </article>
          )}
        </>
      )}
    </div>
  );
}
